import React, {useMemo} from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import Customer from 'coreClasses/customer/Customer';
import {EEventSource} from 'coreClasses/events/IEvents';
import {useCustomerMgr, useNotify} from 'coreHooks';
import {parseBoolean} from 'coreUtils/utils';
import validationRules from 'coreUtils/validationRules';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Field from 'brandComponents/Form/Subcomponents/Field';
import Link from 'brandComponents/Link';
import NewsletterFlags from 'brandComponents/NewsletterFlags';
import {NEWSLETTER_SOURCE} from 'brandConstants';
import {useURLs} from 'brandHooks';

import {INewsletterFormProps} from './INewsletterForm';
import messages from './messages';

import styles from './NewsletterForm.module.scss';

const NewsletterForm = ({classNames}: INewsletterFormProps) => {
	const intl = useIntl();
	const {managerRepository} = useGeneralProvider();
	const notify = useNotify();
	const urls = useURLs();
	const customerMgr = useCustomerMgr();
	const customer: Customer = customerMgr.getCurrentCustomer();
	const initialValues = useMemo(() => ({
		email: customer?.email,
		newsletterSubscriptions: customer?.newsletterSubscriptions?.reduce((c, a) => ({...c, [a]: true}), {}) || {},
		authorizationDataCollection: customer?.authorizationDataCollection !== undefined
			? customer?.authorizationDataCollection?.toString()
			: undefined,
		acceptsMarketing: customer?.newsletter !== undefined
			? customer?.newsletter?.toString()
			: undefined
	}), [customer]);

	const nlSubscriptionSrc: string = EEventSource[EEventSource.NEWSLETTER_POPUP];

	const handleSubscribe = async (values: Record<string, any>) => {
		const sendValues = {...values};
		const {gender, email, newsletterSubscriptions, authorizationDataCollection, acceptsMarketing} = sendValues;
		const subscriptions = newsletterSubscriptions &&
			Object.keys(newsletterSubscriptions)?.reduce((c, k) => {
				if (newsletterSubscriptions[k]) {
					c.push(k);
				}

				return c;
			}, []);

		const newsletterSource = {
			newsletterSource: NEWSLETTER_SOURCE[nlSubscriptionSrc],
			newsletterSourceDetails: {
				pageType: NEWSLETTER_SOURCE.CUSTOM_POPUP
			}
		};

		try {
			const {subscriptionCode} = await customerMgr.subscribeToNewsletter(
				email,
				gender,
				EEventSource.NEWSLETTER_POPUP,
				newsletterSource,
				subscriptions,
				parseBoolean(authorizationDataCollection),
				parseBoolean(acceptsMarketing),
			);

			switch (subscriptionCode) {
				case 'OK':
					notify.success(intl.formatMessage(messages.success));

					break;
				default:
					notify.error(intl.formatMessage(messages.failure));
			}
		} catch {
			notify.error(intl.formatMessage(messages.failure));
		}

		managerRepository.getPopupsMgr().closePopup('newsletter');
	};

	return (
		<Form
			initialValues={initialValues}
			onSubmit={(values: {privacy: any}) => handleSubscribe(values)}
			render={({handleSubmit, submitting, hasValidationErrors}) => {
				return (
					<form className={cn(classNames, styles['newsletter-form'])} onSubmit={handleSubmit}>
						<Field
							id="email"
							name="email"
							type="text"
							component="input"
							label={intl.formatMessage(messages.emailPlaceholder)}
							requirednessLabel="*"
							validation={[
								{
									rule: validationRules.isRequired,
									errorMessage: intl.formatMessage(messages.emailRequired),
								},
								{
									rule: validationRules.isEmail,
									errorMessage: intl.formatMessage(messages.emailError),
								},
							]}
						/>
						<div>
							{intl.formatMessage(messages.privacyPolicyAndTOS, {
								p: (chunk) => <p>{chunk}</p>,
								// TODO: Set correct links with this FER-118 ticket
								link1: (chunk) => (
									<a href={urls.GOOGLE_PRIVACY_POLICY} target="_blank" rel="noreferrer">
										{chunk}
									</a>),
								link2: (chunk) => (
									<a href={urls.GOOGLE_TERMS} target="_blank" rel="noreferrer">
										{chunk}
									</a>),
								link3: (chunk) => (<Link to="/privacy-policy">{chunk}</Link>),
							})}
						</div>
						<NewsletterFlags />
						<Button
							type="submit"
							classNames={styles['newsletter-form__cta']}
							variant="primary-black"
							disabled={submitting || hasValidationErrors}
						>
							{intl.formatMessage(messages.cta)}
						</Button>
					</form>
				);
			}}
		/>
	);
};

export default NewsletterForm;
